import React from "react";
import Button from "@mui/joy/Button";
import Stack from "@mui/joy/Stack";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import Typography from "@mui/joy/Typography";
import Divider from "@mui/joy/Divider";
import Sheet from "@mui/joy/Sheet";
import Box from "@mui/joy/Box";
import Input from "@mui/joy/Input";
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Checkbox from "@mui/joy/Checkbox";

import { AdminGameFilterInfo } from "../../models/gameFilters";
import {
    normalizeColor,
    parseNullableNumber,
    scalarToString,
    stripColorHash
} from "./gameFiltersUtils";

type Props = {
    filter: AdminGameFilterInfo;
    open: boolean;
    setOpen: (open: boolean) => void;
    onConfirm: (updated: AdminGameFilterInfo) => void;
};

export const GameFilterEditorDialog: React.FC<Props> = (props) => {
    const { filter, open, setOpen, onConfirm } = props;

    const [titleRu, setTitleRu] = React.useState(filter.titleRu ?? '');
    const [titleEn, setTitleEn] = React.useState(filter.titleEn ?? '');
    const [icon, setIcon] = React.useState(filter.icon ?? '');

    const [min, setMin] = React.useState<string>(scalarToString(filter.min ?? ''));
    const [max, setMax] = React.useState<string>(scalarToString(filter.max ?? ''));
    const [color, setColor] = React.useState(filter.color ?? '');
    const [textColor, setTextColor] = React.useState(filter.textColor ?? '');
    const [order, setOrder] = React.useState<string>(scalarToString(filter.order));
    const [defaultValue, setDefaultValue] = React.useState<boolean>(!!filter.defaultValue);

    React.useEffect(() => {
        setTitleRu(filter.titleRu ?? '');
        setTitleEn(filter.titleEn ?? '');
        setIcon(filter.icon ?? '');
        setMin(scalarToString(filter.min ?? ''));
        setMax(scalarToString(filter.max ?? ''));
        setColor(filter.color ?? '');
        setTextColor(filter.textColor ?? '');
        setOrder(scalarToString(filter.order));
        setDefaultValue(!!filter.defaultValue);
    }, [filter]);

    return (
        <Modal open={open} onClose={() => setOpen(false)}>
            <ModalDialog
                sx={{
                    maxWidth: 640,
                    width: { xs: 'calc(100% - 32px)', sm: 640 },
                    mx: 'auto',
                    my: { xs: 2, sm: 4 },
                    maxHeight: '90vh',
                    overflow: 'auto',
                }}
            >
                <ModalClose/>
                <Typography level="h2">
                    Редактировать фильтр #{filter.id}
                </Typography>
                <Divider sx={{my: 2}}/>
                <Sheet sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Название (RU)</FormLabel>
                            <Input value={titleRu} onChange={(e) => setTitleRu(e.target.value)} />
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Название (EN)</FormLabel>
                            <Input value={titleEn} onChange={(e) => setTitleEn(e.target.value)} />
                        </FormControl>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Иконка (URL)</FormLabel>
                            <Input value={icon} onChange={(e) => setIcon(e.target.value)} />
                        </FormControl>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Min</FormLabel>
                            <Input value={min} onChange={(e) => setMin(e.target.value)} />
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Max</FormLabel>
                            <Input value={max} onChange={(e) => setMax(e.target.value)} />
                        </FormControl>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Цвет заливки</FormLabel>
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
                            >
                                <Input
                                    value={color}
                                    onChange={(e) => setColor(e.target.value)}
                                    sx={{ flex: 1, minWidth: 0 }}
                                />
                                <Box
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: '4px',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        backgroundColor: normalizeColor(color) || 'transparent',
                                    }}
                                />
                            </Stack>
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Цвет текста</FormLabel>
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
                            >
                                <Input
                                    value={textColor}
                                    onChange={(e) => setTextColor(e.target.value)}
                                    sx={{ flex: 1, minWidth: 0 }}
                                />
                                <Typography
                                    level="h4"
                                    sx={{
                                        fontWeight: 700,
                                        color: normalizeColor(textColor) || 'inherit',
                                        borderRadius: '4px',
                                        px: 0.5,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    A
                                </Typography>
                            </Stack>
                        </FormControl>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Сортировка</FormLabel>
                            <Input value={order} onChange={(e) => setOrder(e.target.value)} />
                        </FormControl>
                        {filter.filterType === 4 && (
                            <Checkbox
                                label="Выбран по умолчанию"
                                checked={defaultValue}
                                onChange={(e) => setDefaultValue(e.target.checked)}
                                sx={{ alignSelf: { sm: 'flex-end' } }}
                            />
                        )}
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', mt: 1 }}>
                        <Button onClick={() => setOpen(false)} color="neutral">Отмена</Button>
                        <Button
                            onClick={() => {
                                const next: AdminGameFilterInfo = {
                                    ...filter,
                                    titleRu: titleRu.trim() ? titleRu : null,
                                    titleEn: titleEn.trim() ? titleEn : null,
                                    icon: icon.trim() ? icon.trim() : null,
                                    min: parseNullableNumber(min),
                                    max: parseNullableNumber(max),
                                    color: stripColorHash(normalizeColor(color)) || null,
                                    textColor: stripColorHash(normalizeColor(textColor)) || null,
                                    order: (parseNullableNumber(order) ?? filter.order) as any,
                                    defaultValue,
                                };
                                setOpen(false);
                                onConfirm(next);
                            }}
                            color="primary"
                        >
                            Сохранить
                        </Button>
                    </Stack>
                </Sheet>
            </ModalDialog>
        </Modal>
    );
};

